import * as React from 'react';
import { useEffect, useReducer } from 'react';

import { Modal, FormGroup, Label, ModalBody, ModalFooter, Button, NavLink, Row, Col } from 'reactstrap';
import CurrencyInput from 'react-currency-input';

import { ShoppingCart } from '../../models/ShoppingCart';
import { ProductInventoryInfo, PartnerStockRow } from '../../models/Product';

import CartService from '../../services/CartService';
import { GetQuantityResult } from '../../services/ProductInventoryService';

import * as CartStore from '../../store/Cart';
import * as AuthorizationStore from '../../store/Authorization';

import { buildMailTo } from './ProductMailHelper';
import * as Analytics from '../../AnalyticsHelper';

import ShoppingCartGenericDropdownAsync from '../../components/controls/ShoppingCartGenericDropdownAsync';

import CreateNewCartModal from './CreateNewCartModal';



type State = {
    unitsToBuy: number;
    unitsToApprove: number;
    step: 'start' | 'approve' | 'end';
    approvedBy: string;
    selectedCart: ShoppingCart;
    itemsLikeThisInCart: number;
    openCreateCartDlg: boolean;
}

const initialState: State = {
    unitsToBuy: 1,
    unitsToApprove: 0,
    step: 'start',
    approvedBy: '',
    selectedCart: undefined,
    itemsLikeThisInCart: 0,
    openCreateCartDlg: false
}

function AddProductCardInfoModal(props: {
    inventory: ProductInventoryInfo,
    partnerStock: PartnerStockRow,
    cartState: CartStore.IState,
    productQuantity: GetQuantityResult,
    unitsAddedToCart: number,
    userId: string,
    onCancel: () => void,
    onAdd: (amount: number, approvedBy: string, cart: ShoppingCart) => void
}) {

    let [state, setState] = useReducer(
        (prevState: State, newState: Partial<State>) => ({ ...prevState, ...newState }),
        initialState
    );

    const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();

    const calculateItemsLikeThisInCart = (cartId: string, productNumber: string, partnerId: string) => {

        let amount = 0;
        const cart = props.cartState.shoppingCarts.find(x => x.CartId === cartId);
        if (cart) {
            const itemInCart = cart.Items.find(x => x.PartnerId === partnerId && x.ProductNumber === productNumber);
            if (itemInCart)
                amount = itemInCart.Amount;
        }

        return amount;
    }

    const calcNewDesiredValue = (desired: number) => {

        desired = Math.max(desired, 0);

        let qty = props.productQuantity.available + props.productQuantity.callToBuy;
        if (props.partnerStock && props.partnerStock.UseOnOrder) {
            qty += props.productQuantity.onOrder;
        }

        var max = qty - state.itemsLikeThisInCart;

        if (max > 0 && desired === 0)
            desired = 1;

        var clampedValue = Math.min(desired, max);

        setState({ unitsToBuy: clampedValue });
    }

    const canAddToCart = () => {
        const available = props.productQuantity.available;
        const callToBuy = props.productQuantity.callToBuy;
        const onOrder = props.productQuantity.onOrder;

        let qty = available + callToBuy;

        if (props.partnerStock.UseOnOrder) {
            qty += onOrder;
        }

        return state.unitsToBuy + state.itemsLikeThisInCart <= qty;
    }

    const cancel = () => {
        setState({
            selectedCart: undefined,
            itemsLikeThisInCart: 0
        });

        props.onCancel();
    }

    useEffect(() => {

        // do a reset
        if (props.partnerStock && props.productQuantity) {
            setState({
                step: 'start',
                approvedBy: ''
            })

            calcNewDesiredValue(1);
        }

    }, [props.partnerStock, props.productQuantity]);

    return <>
        {props.partnerStock && <Modal backdrop={'static'} isOpen={props.partnerStock !== undefined} toggle={cancel} size="md" className="modal-dialog-centered">
            <div className="modal-header">
                {state.step === 'approve' && <button className="go-back" type="button" onClick={() => {
                    setState({ step: 'start' })
                }}><i className="fa fa-angle-left"></i></button>
                }<h5 className="modal-title">Add to Cart</h5>
                <button type="button" className="close" aria-label="Close" onClick={cancel}><span aria-hidden="true"><i className="fa fa-times"></i></span></button>
            </div>

            <ModalBody>
                <div className="form-group">
                    <label>PRODUCT</label>
                    <span className="form-control-plaintext">{props.inventory.ProductNumber}</span>
                    {props.partnerStock.OrderCutoffTime && props.partnerStock.OrderCutoffTime.length > 0 &&
                        <span className="form-control-plaintext p-0"><strong>Order Cutoff:</strong> {props.partnerStock.OrderCutoffTime}</span>
                    }
                    {props.partnerStock.OrderProcessing && props.partnerStock.OrderProcessing.length > 0 &&
                        <span className="form-control-plaintext p-0"><strong>Order Processing:</strong> {props.partnerStock.OrderProcessing}</span>
                    }
                </div>
                <hr />

                {state.step === 'start' && <>
                    <div className="form-group">
                        <label>IMPACT PARTNER</label>
                        <span className="form-control-plaintext">{props.partnerStock.PartnerName}</span>
                    </div>
                    <hr />

                    <div className="d-flex">
                        {
                            props.productQuantity &&
                            <React.Fragment>
                                <div className="section-col">
                                    <label>AVAILABLE</label>
                                    <div className="form-control-plaintext">{props.productQuantity.available}</div>
                                </div>
                                {props.partnerStock.UseOnOrder &&
                                    <div className="section-col">
                                        <label>ON ORDER</label>
                                        <div className="form-control-plaintext">{props.productQuantity.onOrder}</div>
                                    </div>
                                }
                                <div className="section-col">
                                    <label>CALL TO BUY</label>
                                    <div className="form-control-plaintext">{props.productQuantity.callToBuy}</div>
                                </div>
                            </React.Fragment>
                        }
                        <div className="section-col">
                            <label>COST</label>
                            <div className="form-control-plaintext"><span>{new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                            }).format(props.partnerStock.PricePerUnit)}</span><span className="per-unit"> / unit</span></div>
                        </div>
                        <div className="section-col">
                            <label>&nbsp;</label>
                            <NumericInput value={state.unitsToBuy}
                                onDescrease={() => calcNewDesiredValue(state.unitsToBuy - 1)}
                                onIncrease={() => calcNewDesiredValue(state.unitsToBuy + 1)}
                                handleChange={(event, maskedvalue, floatvalue) => { calcNewDesiredValue(maskedvalue) }}
                            />
                        </div>
                    </div>
                    <hr />
                    <FormGroup className="mb-2 mr-sm-2 mb-sm-0">
                        <Label className="mr-sm-2">
                            Select Cart
                        </Label>
                        <ShoppingCartGenericDropdownAsync
                            loadDataSource={() => CartService.getLookupCarts(props.userId, partner ? partner.Id : undefined, null, true)}
                            getId={x => x.CartId}
                            renderItem={x => x.CartId === '-1' ? <strong>{x.CartName}</strong> : x.CartName}
                            renderSelectedItem={x => x ? x.CartName : ''}
                            allowNullSelection={false}
                            nullSelectionText='Select Cart'
                            selectedValue={state.selectedCart ? state.selectedCart.CartId : undefined}
                            onSelectedItemChanged={x => {

                                const itemsLikeThis = x ? calculateItemsLikeThisInCart(x.CartId, props.inventory.ProductNumber, props.partnerStock.PartnerId) : 0;
                                setState({ selectedCart: x, itemsLikeThisInCart: itemsLikeThis });

                                if (x && x.CartId === "-1") {
                                    setState({ openCreateCartDlg: true });
                                }
                            }}
                        />
                    </FormGroup>
                </>}

                {state.step === 'approve' && <div className="text-center">
                    <div><i className="fa fa-question-circle fa-3x color-orange"></i></div>
                    <div className="lead"><span>Some of the quantity you've selected ({state.unitsToApprove}) requires call for approval</span></div>
                    <hr />
                    <div className="partner-name text-nowrap">{props.partnerStock.PriceAvailabilityContactName}</div>
                    <div><a href={buildMailTo(props.partnerStock, props.inventory, props.productQuantity)} className="text-nowrap">{props.partnerStock.PriceAvailabilityContactEmail}</a></div>
                    <div className="text-nowrap">
                        <a href={'tel:' + props.partnerStock.PriceAvailabilityContactPhone} className="text-nowrap">{props.partnerStock.PriceAvailabilityContactPhone}</a>
                    </div>
                </div>}

            </ModalBody>
            <ModalFooter>
                {state.step === "start" &&
                    <>
                        {
                            state.itemsLikeThisInCart > 0 &&
                            <div className="text-center w-100" style={{ marginTop: '-5px' }}>
                                {state.itemsLikeThisInCart} {state.itemsLikeThisInCart > 1 ? 'items' : 'item'}  in cart
                            </div>
                        }

                        <Button color="black" className="w-100" onClick={() => {

                            const goToApprove = (state.unitsToBuy + state.itemsLikeThisInCart) > props.productQuantity.available;

                            const unitsToApprove = state.unitsToBuy + state.itemsLikeThisInCart - props.productQuantity.available;

                            if (goToApprove) {
                                setState({
                                    step: 'approve',
                                    unitsToApprove: unitsToApprove,
                                });
                            } else {
                                Analytics.push({
                                    'event': 'inet-cart',
                                    'product-number': props.inventory.ProductNumber
                                });

                                props.onAdd(state.unitsToBuy, state.approvedBy, state.selectedCart);
                                setState({
                                    selectedCart: undefined,
                                    itemsLikeThisInCart: 0
                                });
                            }

                    }} disabled={state.unitsToBuy === 0 || state.selectedCart === undefined || !canAddToCart()}>ADD TO CART</Button>
                    </>
                }

                {state.step === 'approve' && <Row className="w-100">
                    <Col md={7} className="p-0">
                        <div className="form-group m-0">
                            <label className="text-nowrap">Who Approved The Purchase?</label>
                            <input className="form-control" value={state.approvedBy} onChange={(e) => setState({ approvedBy: e.target.value })} />
                        </div>
                    </Col>
                    <Col md={5} className="pr-0">
                        <div className="form-group m-0">
                            <label>&nbsp;</label>
                            <Button color="black" className="w-100" onClick={() => {
                                Analytics.push({
                                    'event': 'inet-cart',
                                    'product-number': props.inventory.ProductNumber
                                });
                                props.onAdd(state.unitsToBuy, state.approvedBy, state.selectedCart);
                                setState({
                                    selectedCart: undefined,
                                    itemsLikeThisInCart: 0
                                })
                            }} disabled={state.approvedBy.length === 0}>Approved</Button>
                        </div>
                    </Col>
                </Row>}
            </ModalFooter>
        </Modal>}
        {
            <CreateNewCartModal open={state.openCreateCartDlg}
                onCancel={() => setState({
                    openCreateCartDlg: false,
                    selectedCart: undefined
                })}
                onAdd={(name) => {

                    const goToApprove = state.unitsToBuy > props.productQuantity.available;

                    const unitsToApprove = state.unitsToBuy - props.productQuantity.available;

                    const newCart = { CartId: undefined, CartName: name, UserId: undefined };

                    if (goToApprove) {
                        setState({
                            step: 'approve',
                            unitsToApprove: unitsToApprove,
                            openCreateCartDlg: false,
                            selectedCart: newCart
                        });
                    }
                    else {
                        Analytics.push({
                            'event': 'inet-cart',
                            'product-number': props.inventory.ProductNumber
                        });

                        props.onAdd(state.unitsToBuy, state.approvedBy, newCart);

                        setState({
                            openCreateCartDlg: false,
                            selectedCart: undefined,
                            itemsLikeThisInCart: 0
                        })
                    }
                }}
            />
        }
    </>

}


function NumericInput(props: { value: number, onIncrease: () => void, onDescrease: () => void, handleChange: (event, maskedvalue, floatvalue) => void }) {

    return <div className="numeric-input form-control-plaintext d-flex">
        <button type="button" className="qty-minus" onClick={() => props.onDescrease()}>-</button>
        <CurrencyInput value={props.value} precision={0} className="flex-grow-1" onChangeEvent={props.handleChange} />
        <button type="button" onClick={() => props.onIncrease()} className="qty-plus">+</button></div>
}

export default AddProductCardInfoModal;
